import type { JSONContent } from '@tiptap/core';
import type { Schema } from '@tiptap/pm/model';

import { type Rgb, TEXT_COLOR_PALETTE } from 'containers/Tiptap/lib/palette-colors';

// Rewrites Tiptap's .docx import JSON into our editor's semantic vocabulary:
// heading-in-list & H4–H6 → paragraph, bogus bold+italic dropped, size → heading, no px.

const LIST_ITEM_TYPES = new Set(['listItem', 'taskItem']);
const TABLE_CELL_TYPES = new Set(['tableCell', 'tableHeader']);

// How much larger than body a run must be to count as a heading (keeps
// ordinary 11pt/12pt body variation out of the heading bands).
const HEADING_SIZE_RATIO = 1.2;
const MAX_IMPORT_HEADING_LEVEL = 3;
// Assumed body size when a document leaves its body text unsized and only
// sizes its headings, so large runs still promote (~Word default 11–12pt).
const DEFAULT_BODY_PX = 16;

type Attrs = Record<string, unknown>;

function demoteHeadingToParagraph(node: JSONContent): JSONContent {
  // Strip heading-only attrs (level, etc.) — paragraphs don't carry them.
  const { attrs: _attrs, ...rest } = node;
  return { ...rest, type: 'paragraph' };
}

function textHasMark(node: JSONContent, markType: string): boolean {
  return Array.isArray(node.marks) && node.marks.some((m) => m.type === markType);
}

function stripMarksFromText(node: JSONContent, markTypes: ReadonlySet<string>): JSONContent {
  if (node.type !== 'text' || !Array.isArray(node.marks)) return node;
  const remaining = node.marks.filter((m) => !markTypes.has(m.type));
  if (remaining.length === node.marks.length) return node;
  if (remaining.length === 0) {
    const { marks: _marks, ...rest } = node;
    return rest;
  }
  return { ...node, marks: remaining };
}

// True if every direct text child carries *all* the listed marks. Non-text
// children are ignored; an empty paragraph is not "uniform".
function isParagraphUniformlyMarked(paragraph: JSONContent, markTypes: readonly string[]): boolean {
  if (paragraph.type !== 'paragraph' || !Array.isArray(paragraph.content)) return false;
  const texts = paragraph.content.filter((c) => c?.type === 'text');
  if (texts.length === 0) return false;
  return texts.every((t) => markTypes.every((m) => textHasMark(t, m)));
}

const BOGUS_PARAGRAPH_MARK_LIST = ['bold', 'italic'] as const;
const BOGUS_PARAGRAPH_MARKS = new Set<string>(BOGUS_PARAGRAPH_MARK_LIST);

// The bogus mark is a document-wide converter default; authored bold+italic is
// localised. Only strip when uniform bold+italic is this pervasive (ENG-11576).
const BOGUS_BOLD_ITALIC_PREVALENCE = 0.9;

function shouldDropUniformBoldItalic(doc: JSONContent): boolean {
  let total = 0;
  let uniform = 0;
  const visit = (node?: JSONContent): void => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'paragraph' && Array.isArray(node.content) && node.content.some((c) => c?.type === 'text')) {
      total += 1;
      if (isParagraphUniformlyMarked(node, BOGUS_PARAGRAPH_MARK_LIST)) uniform += 1;
    }
    node.content?.forEach(visit);
  };
  visit(doc);
  return total > 0 && uniform / total >= BOGUS_BOLD_ITALIC_PREVALENCE;
}

function dropBogusParagraphWideMarks(paragraph: JSONContent): JSONContent {
  if (!isParagraphUniformlyMarked(paragraph, BOGUS_PARAGRAPH_MARK_LIST)) return paragraph;
  if (!Array.isArray(paragraph.content)) return paragraph;
  const cleaned = paragraph.content.map((child) =>
    child?.type === 'text' ? stripMarksFromText(child, BOGUS_PARAGRAPH_MARKS) : child
  );
  return { ...paragraph, content: cleaned };
}

// Parse a CSS font-size to px. Tiptap normalises Word's points to px (e.g.
// "16px"); we also accept "pt" defensively (1pt = 96/72 px).
function parseFontSizePx(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = /^([\d.]+)\s*(px|pt)?$/i.exec(value.trim());
  if (!match) return null;
  const n = parseFloat(match[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  return match[2]?.toLowerCase() === 'pt' ? n * (96 / 72) : n;
}

function textStyleFontSizePx(text: JSONContent): number | null {
  if (!Array.isArray(text.marks)) return null;
  const textStyle = text.marks.find((m) => m.type === 'textStyle');
  return textStyle?.attrs ? parseFontSizePx((textStyle.attrs as Attrs).fontSize) : null;
}

// The size shared by every text run, or null if empty/unsized/mixed — mixed
// sizing (one enlarged word) must not promote the whole paragraph.
function paragraphUniformFontSizePx(paragraph: JSONContent): number | null {
  if (!Array.isArray(paragraph.content)) return null;
  const texts = paragraph.content.filter((c) => c?.type === 'text');
  if (texts.length === 0) return null;
  const sizes = texts.map(textStyleFontSizePx);
  const first = sizes[0];
  if (first == null) return null;
  return sizes.every((s) => s === first) ? first : null;
}

// Per-document size→level map: body = the most-weighted paragraph-run size
// (unsized runs counted under px 0); sizes ≥ body×ratio rank into H1/H2/H3.
function buildHeadingSizeMap(doc: JSONContent): Map<number, number> {
  const weightByPx = new Map<number, number>(); // px 0 == an unsized run

  const visit = (node: JSONContent | undefined, parentType: string | undefined): void => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'text') {
      // Only paragraph runs inform the body baseline; heading runs are excluded.
      if (parentType === 'paragraph') {
        const px = textStyleFontSizePx(node) ?? 0;
        const weight = typeof node.text === 'string' ? Math.max(node.text.length, 1) : 1;
        weightByPx.set(px, (weightByPx.get(px) ?? 0) + weight);
      }
      return;
    }
    if (Array.isArray(node.content)) {
      node.content.forEach((child) => visit(child, node.type));
    }
  };
  visit(doc, undefined);

  const sizedPx = [...weightByPx.keys()].filter((px) => px > 0);
  if (sizedPx.length === 0) return new Map();

  let bodyPx = Infinity;
  let bodyWeight = -1;
  weightByPx.forEach((weight, px) => {
    // Most-weighted size wins; ties resolve to the smaller size (body is small + common).
    if (weight > bodyWeight || (weight === bodyWeight && px < bodyPx)) {
      bodyWeight = weight;
      bodyPx = px;
    }
  });
  if (bodyPx === 0) bodyPx = DEFAULT_BODY_PX; // body is unsized → assume the default

  const larger = sizedPx.filter((px) => px >= bodyPx * HEADING_SIZE_RATIO).sort((a, b) => b - a);
  const map = new Map<number, number>();
  larger.forEach((px, index) => map.set(px, Math.min(index + 1, MAX_IMPORT_HEADING_LEVEL)));
  return map;
}

const FONT_ATTR_KEYS = ['fontSize', 'fontFamily'] as const;

const MONOSPACE_FONT = /^(courier(\s+new)?|consolas|monaco|menlo|lucida\s+console|monospace|.+\s(mono|code)(\s+pro)?)$/i;

// Monospace as the body font (an old contract in Courier) is the document's
// style, not inline code.
const MONOSPACE_BODY_PREVALENCE = 0.5;

function isMonospaceFontFamily(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const first = value.split(',')[0].trim().replace(/^["']|["']$/g, '');
  return MONOSPACE_FONT.test(first);
}

function isMonospaceRun(text: JSONContent): boolean {
  return (
    Array.isArray(text.marks) &&
    text.marks.some((m) => m.type === 'textStyle' && isMonospaceFontFamily((m.attrs as Attrs | undefined)?.fontFamily))
  );
}

function shouldConvertToInlineCode(text: JSONContent, monospaceIsBody: boolean): boolean {
  return !monospaceIsBody && isMonospaceRun(text) && !textHasMark(text, 'code');
}

function isMonospaceBodyFont(doc: JSONContent): boolean {
  let total = 0;
  let monospace = 0;
  const visit = (node?: JSONContent): void => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'text' && typeof node.text === 'string') {
      total += node.text.length;
      if (isMonospaceRun(node)) monospace += node.text.length;
    }
    node.content?.forEach(visit);
  };
  visit(doc);
  return total > 0 && monospace / total >= MONOSPACE_BODY_PREVALENCE;
}

// Colours below this on every channel are treated as default body text and
// dropped — the converter stamps a near-black colour on essentially every run.
const NEAR_BLACK_MAX_CHANNEL = 48;

// Further from every swatch than this, an imported colour keeps its source hex.
const MAX_SNAP_DISTANCE = 60;

// Word renders automatic text white on shading darker than this (perceived luminance, 0–255).
const DARK_BACKGROUND_MAX_LUMINANCE = 128;
const TEXT_ON_DARK_BACKGROUND = '#FFFFFF';

function parseHexRgb(value: string): Rgb | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const hex = match[1].length === 3 ? match[1].replace(/(.)/g, '$1$1') : match[1];
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

function isDarkBackground(rgb: Rgb | null): boolean {
  return rgb !== null && 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] < DARK_BACKGROUND_MAX_LUMINANCE;
}

function cellBackgroundRgb(cell: JSONContent): Rgb | null {
  const attrs = cell.attrs as Attrs | undefined;
  const value = attrs?.backgroundColor ?? attrs?.background;
  return typeof value === 'string' ? parseHexRgb(value) : null;
}

// Returns the nearest palette CSS variable for an imported colour, `null` to
// drop it (near-black ≈ default text), or the colour itself when no swatch is close.
function mapColorToPalette(value: unknown, background: Rgb | null): string | null | undefined {
  if (typeof value !== 'string') return undefined;
  const rgb = parseHexRgb(value);
  if (!rgb) return value; // already a var() / unrecognised — leave as-is
  if (Math.max(...rgb) < NEAR_BLACK_MAX_CHANNEL) return isDarkBackground(background) ? TEXT_ON_DARK_BACKGROUND : null;
  let best = TEXT_COLOR_PALETTE[0];
  let bestDistance = Infinity;
  for (const swatch of TEXT_COLOR_PALETTE) {
    const d = (rgb[0] - swatch.rgb[0]) ** 2 + (rgb[1] - swatch.rgb[1]) ** 2 + (rgb[2] - swatch.rgb[2]) ** 2;
    if (d < bestDistance) {
      bestDistance = d;
      best = swatch;
    }
  }
  if (bestDistance > MAX_SNAP_DISTANCE ** 2) return value;
  return `var(--tt-color-text-${best.name})`;
}

// Normalise a text run's textStyle mark to the editor's vocabulary: drop
// fontSize/fontFamily (no UI control), snap a near-swatch colour to its palette
// variable (or drop near-black), and drop the mark if nothing remains. Monospace
// runs also get inline code, keeping their other marks.
function normalizeTextRunMarks(text: JSONContent, background: Rgb | null, monospaceIsBody: boolean): JSONContent {
  if (text.type !== 'text' || !Array.isArray(text.marks)) return text;
  const normalized = normalizeTextStyleAttrs(text, background);
  if (!shouldConvertToInlineCode(text, monospaceIsBody)) return normalized;
  return { ...normalized, marks: [...(normalized.marks ?? []), { type: 'code' }] };
}

function normalizeTextStyleAttrs(text: JSONContent, background: Rgb | null): JSONContent {
  if (!Array.isArray(text.marks)) return text;
  let changed = false;
  const marks = text.marks.flatMap((mark) => {
    if (mark.type !== 'textStyle' || !mark.attrs) return [mark];
    const attrs: Attrs = { ...(mark.attrs as Attrs) };
    let touched = false;
    for (const key of FONT_ATTR_KEYS) {
      if (key in attrs) {
        delete attrs[key];
        touched = true;
      }
    }
    if ('color' in attrs) {
      const mapped = mapColorToPalette(attrs.color, background);
      if (mapped === null) {
        delete attrs.color;
        touched = true;
      } else if (mapped !== undefined && mapped !== attrs.color) {
        attrs.color = mapped;
        touched = true;
      }
    }
    if (!touched) return [mark];
    changed = true;
    return Object.keys(attrs).length > 0 ? [{ ...mark, attrs }] : [];
  });
  if (!changed) return text;
  if (marks.length === 0) {
    const { marks: _marks, ...rest } = text;
    return rest;
  }
  return { ...text, marks };
}

// Drop a mirrored fontSize/fontFamily from a block node's attrs (the converter
// sometimes copies the run size onto the paragraph).
function stripFontAttrs(node: JSONContent): JSONContent {
  if (!node.attrs || typeof node.attrs !== 'object') return node;
  const attrs = node.attrs as Attrs;
  if (!FONT_ATTR_KEYS.some((k) => k in attrs)) return node;
  const { fontSize: _fs, fontFamily: _ff, ...rest } = attrs;
  if (Object.keys(rest).length === 0) {
    const { attrs: _attrs, ...node_rest } = node;
    return node_rest;
  }
  return { ...node, attrs: rest };
}

function promoteParagraphToHeading(paragraph: JSONContent, level: number): JSONContent {
  const attrs: Attrs = { level };
  const align = (paragraph.attrs as Attrs | undefined)?.textAlign;
  if (align) attrs.textAlign = align;
  const { attrs: _attrs, ...rest } = paragraph;
  return { ...rest, type: 'heading', attrs };
}

// Word's TOC field converts to an empty `tableOfContents` marker; our own
// `tocNode` builds its entries live from the document's headings.
function toTocNode(): JSONContent {
  return { type: 'tocNode' };
}

function transform(
  node: JSONContent,
  isTopLevel: boolean,
  headingMap: Map<number, number>,
  dropUniformBoldItalic: boolean,
  background: Rgb | null,
  monospaceIsBody: boolean
): JSONContent {
  if (!node || typeof node !== 'object') return node;
  if (node.type === 'text') return normalizeTextRunMarks(node, background, monospaceIsBody);
  if (node.type === 'tableOfContents') return toTocNode();

  let working = node;

  if (Array.isArray(working.content)) {
    const isListItemParent = typeof working.type === 'string' && LIST_ITEM_TYPES.has(working.type);
    const childIsTopLevel = working.type === 'doc';
    const childBackground =
      typeof working.type === 'string' && TABLE_CELL_TYPES.has(working.type) ? cellBackgroundRgb(working) : background;
    const content = working.content.map((child) => {
      const pre = isListItemParent && child?.type === 'heading' ? demoteHeadingToParagraph(child) : child;
      return transform(pre, childIsTopLevel, headingMap, dropUniformBoldItalic, childBackground, monospaceIsBody);
    });
    working = { ...working, content };
  }

  working = stripFontAttrs(working);

  if (working.type === 'paragraph') {
    if (dropUniformBoldItalic) working = dropBogusParagraphWideMarks(working);
    if (isTopLevel) {
      // Measure size from the ORIGINAL node — `working`'s runs are font-stripped.
      const size = paragraphUniformFontSizePx(node);
      const level = size != null ? headingMap.get(size) : undefined;
      if (level) working = promoteParagraphToHeading(working, level);
    }
    return working;
  }

  if (working.type === 'heading') {
    const level = Number((working.attrs as Attrs | undefined)?.level);
    if (Number.isFinite(level) && level > MAX_IMPORT_HEADING_LEVEL) {
      return demoteHeadingToParagraph(working);
    }
    return working;
  }

  return working;
}

export function sanitizeImportedContent(node: JSONContent): JSONContent {
  if (!node || typeof node !== 'object') return node;
  const headingMap = buildHeadingSizeMap(node);
  return transform(node, true, headingMap, shouldDropUniformBoldItalic(node), null, isMonospaceBodyFont(node));
}

function stripFontFromTextStyle(text: JSONContent): JSONContent {
  if (text.type !== 'text' || !Array.isArray(text.marks)) return text;
  const marks = text.marks.flatMap((mark) => {
    if (mark.type !== 'textStyle' || !mark.attrs) return [mark];
    const { fontSize: _fs, fontFamily: _ff, ...rest } = mark.attrs as Attrs;
    return Object.keys(rest).length > 0 ? [{ ...mark, attrs: rest }] : [];
  });
  if (marks.length === 0) {
    const { marks: _marks, ...rest } = text;
    return rest;
  }
  return { ...text, marks };
}

export function sanitizeHeaderFooterNode(node: JSONContent): JSONContent {
  if (!node || typeof node !== 'object') return node;
  if (node.type === 'text') return stripFontFromTextStyle(node);
  let working = stripFontAttrs(node);
  if (Array.isArray(working.content)) {
    working = { ...working, content: working.content.map(sanitizeHeaderFooterNode) };
  }
  return working.type === 'heading' ? demoteHeadingToParagraph(working) : working;
}

// One unknown node/mark type makes setContent fall back to an EMPTY doc, and one
// block node in an inline slot makes it throw. Unwrap unknown nodes into their
// children, strip unknown marks, and lift stray blocks out of textblocks.
export function pruneToSchema(node: JSONContent, schema: Schema): JSONContent {
  const pruned = pruneNode(node, schema);
  if (pruned.length === 1 && pruned[0].type === node.type) return pruned[0];
  // Root itself was unknown (or vanished) — keep every surviving child.
  return { type: 'doc', content: pruned };
}

function isBlockType(type: string | undefined, schema: Schema): boolean {
  return typeof type === 'string' ? Boolean(schema.nodes[type]?.isBlock) : false;
}

function collapseAdjacentRules(nodes: JSONContent[]): JSONContent[] {
  return nodes.filter((n, i) => n.type !== 'horizontalRule' || nodes[i - 1]?.type !== 'horizontalRule');
}

// The converter puts block nodes inside paragraphs (a Word bottom-border
// paragraph becomes `paragraph > horizontalRule`), which ProseMirror rejects.
// Split the textblock so each block becomes a sibling instead.
function liftBlocksOutOfTextblock(node: JSONContent, schema: Schema): JSONContent[] {
  const children = node.content as JSONContent[];
  if (!children.some((child) => isBlockType(child.type, schema))) return [node];

  const lifted: JSONContent[] = [];
  let inline: JSONContent[] = [];
  const flushInline = (): void => {
    if (inline.length > 0) lifted.push({ ...node, content: inline });
    inline = [];
  };

  children.forEach((child) => {
    if (isBlockType(child.type, schema)) {
      flushInline();
      lifted.push(child);
      return;
    }
    inline.push(child);
  });
  flushInline();

  return collapseAdjacentRules(lifted);
}

function pruneNode(node: JSONContent, schema: Schema): JSONContent[] {
  if (!node || typeof node !== 'object') return [];

  const next: JSONContent = { ...node };

  if (Array.isArray(next.marks)) {
    const known = next.marks.filter((m) => Boolean(schema.marks[m.type]));
    if (known.length > 0) {
      next.marks = known;
    } else {
      delete next.marks;
    }
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.flatMap((child) => pruneNode(child, schema));
  }

  if (typeof next.type === 'string' && !schema.nodes[next.type]) {
    return Array.isArray(next.content) ? next.content : [];
  }

  if (Array.isArray(next.content) && schema.nodes[next.type as string]?.inlineContent) {
    return liftBlocksOutOfTextblock(next, schema);
  }

  return [next];
}
